import { createContext, useContext, useEffect, useRef, useState } from "react";

const LANGUAGE_KEY = "iym_language";

const ID_TO_EN = {
  "Iya Lagi Manjat - Komunitas Pemanjat Kalcer": "Iya Lagi Manjat - Climbing Community",
  "Masuk dulu, yuk": "Welcome back",
  "Climber masuk pakai nomor WhatsApp & PIN.": "Climbers sign in with their WhatsApp number and PIN.",
  "Nomor WhatsApp": "WhatsApp number",
  "PIN (6 digit)": "PIN (6 digits)",
  "Tampilkan password": "Show password",
  "Sembunyikan password": "Hide password",
  "Jika lupa PIN Klik disini": "Forgot your PIN? Click here",
  "Daftar": "Register",
  "Baru di sini? Daftar sekali, waiver cukup disetujui sekali.": "New here? Register once and accept the waiver once.",
  "Keeper / Admin": "Keeper / Admin",
  "Climber": "Climber",
  "Login": "Login",
  "Yuk manjat bareng. Ketemu di wall!": "Let's climb together. See you at the wall!",
  "komunitas climbing": "climbing community",
  "Halo, climber!": "Hello, climber!",
  "QR Saya": "My QR",
  "tunjukkan ke keeper": "show it to the keeper",
  "Kontribusi Kolektif": "Collective Contribution",
  "Kontribusi Kolektif QRIS": "Collective Contribution QRIS",
  "QRIS Komunitas": "Community QRIS",
  "Riwayat Climbing": "Climbing History",
  "Semua climb yang sudah selesai": "All completed climbs",
  "Climber paling rajin": "Most active climbers",
  "Aturan Area": "Area Rules",
  "Climbing Calls": "Climbing Calls",
  "Siap manjat dengan aman": "Climb safely",
  "Buka panduan sebelum mulai naik.": "Open the guide before you start climbing.",
  "panduan di wall": "wall guide",
  "Etika dan keselamatan saat di wall": "Wall etiquette and safety",
  "Panggilan jelas antara pemanjat dan belayer": "Clear calls between climber and belayer",
  "Ragu? Stop. Tanya dulu.": "Unsure? Stop. Ask first.",
  "Di wall ini nggak ada lomba siapa yang paling berani.": "There is no prize for being the bravest at the wall.",
  "Jangan berdiri atau lewat di bawah orang yang sedang manjat.": "Do not stand or walk under a climber.",
  "Jangan pegang atau mainkan tali dan alat yang sedang dipakai.": "Do not touch or play with ropes and equipment in use.",
  "Belum bisa belay? Jangan coba sendiri, minta didampingi tim.": "New to belaying? Do not try alone; ask the team for support.",
  "Ini ruang publik: barang pribadi tanggung jawab masing-masing.": "This is a public space: personal belongings are your own responsibility.",
  "Belayer nggak bisa baca pikiran. Ucapkan dengan suara keras, menghadap belayer, dan tunggu dijawab sebelum naik.": "Belayers cannot read minds. Speak clearly, face your belayer, and wait for a response before climbing.",
  "Selama Manjat": "While Climbing",
  "Pemanjat": "Climber",
  "Belayer": "Belayer",
  "Tolong kendurkan talinya.": "Please give more slack.",
  "Tolong tegangkan talinya. Mau istirahat.": "Please take in the rope. I need a rest.",
  "Aku mau turun.": "I want to come down.",
  "Aku mau jatuh. Teriak sebelum lepas.": "I am falling. Call it before letting go.",
  "Belum ada sesi aktif": "No active session yet",
  "Pantau info sesi berikutnya dari komunitas. Riwayat & leaderboard tetap bisa dilihat.": "Watch the community for the next session. History and leaderboard remain available.",
  "Home": "Home",
  "Riwayat": "History",
  "Profil": "Profile",
  "Notifikasi": "Notifications",
  "Pengaturan": "Settings",
  "Ringkasan": "Overview",
  "Event": "Events",
  "Jalur": "Routes",
  "Peserta": "Participants",
  "Eligibilitas Lead": "Lead Eligibility",
  "Kehadiran": "Attendance",
  "Analitik": "Analytics",
  "Staf / Keeper": "Staff / Keepers",
  "Audit Log": "Audit Log",
  "Buka Mode Keeper": "Open Keeper Mode",
  "Keluar": "Sign out",
  "Buat Event": "Create Event",
  "Publikasikan": "Publish",
  "Tutup Event": "Close Event",
  "Hapus Event": "Delete Event",
  "Reopen Event": "Reopen Event",
  "Aktif": "Active",
  "Ditutup": "Closed",
  "Draft": "Draft",
  "Menunggu": "Waiting",
  "Dipanggil": "Called",
  "Hadir": "Arrived",
  "Sedang Manjat": "Climbing",
  "Selesai": "Completed",
  "Dibatalkan": "Cancelled",
  "Buka Kembali": "Reopen",
  "Pilih event": "Select an event",
  "Check-in": "Check-in",
  "Check-in Manual": "Manual Check-in",
  "Scan QR": "Scan QR",
  "Belum ada jalur": "No routes yet",
  "Admin perlu menambahkan jalur untuk event ini.": "An admin needs to add routes for this event.",
  "Open for Public": "Open for Public",
  "Komunitas / Kontribusi Kolektif": "Public / Collective Contribution",
  "Berbayar": "Paid",
  "Kontribusi Kolektif Komunitas": "Community Collective Contribution",
  "Pengaturan Kontribusi Kolektif disimpan.": "Collective Contribution settings saved.",
  "Penjelasan Kontribusi Kolektif / pesan": "Collective Contribution message",
  "Simpan": "Save",
};

const LanguageContext = createContext(null);

function preserveWhitespace(value, translated) {
  const leading = value.match(/^\s*/)?.[0] || "";
  const trailing = value.match(/\s*$/)?.[0] || "";
  return `${leading}${translated}${trailing}`;
}

function translateValue(value, language) {
  if (language !== "en" || !value) {
    return value;
  }
  const trimmed = value.trim();
  const translated = ID_TO_EN[trimmed];
  return translated ? preserveWhitespace(value, translated) : value;
}

function isTranslatableText(node) {
  const parent = node.parentElement;
  return Boolean(
    parent &&
      !["SCRIPT", "STYLE", "CODE", "PRE"].includes(parent.tagName) &&
      !parent.closest("[data-no-translate]")
  );
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => localStorage.getItem(LANGUAGE_KEY) || "id");
  const originalText = useRef(new WeakMap());
  const originalAttributes = useRef(new WeakMap());

  useEffect(() => {
    localStorage.setItem(LANGUAGE_KEY, language);
    document.documentElement.lang = language;
    document.title = translateValue(
      "Iya Lagi Manjat - Komunitas Pemanjat Kalcer",
      language
    );

    const translate = () => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      const nodes = [];
      let node = walker.nextNode();
      while (node) {
        nodes.push(node);
        node = walker.nextNode();
      }
      nodes.forEach((textNode) => {
        if (!isTranslatableText(textNode)) {
          return;
        }
        const current = textNode.nodeValue || "";
        const source = originalText.current.get(textNode);
        const knownTranslation = source ? translateValue(source, "en") : null;
        if (!source || (current !== source && current !== knownTranslation)) {
          originalText.current.set(textNode, current);
        }
        const original = originalText.current.get(textNode) || current;
        const translated = translateValue(original, language);
        if (textNode.nodeValue !== translated) {
          textNode.nodeValue = translated;
        }
      });

      document.querySelectorAll("[placeholder], [title], [aria-label]").forEach((element) => {
        const saved = originalAttributes.current.get(element) || {};
        ["placeholder", "title", "aria-label"].forEach((attribute) => {
          const current = element.getAttribute(attribute);
          if (!current) {
            return;
          }
          const source = saved[attribute];
          const knownTranslation = source ? translateValue(source, "en") : null;
          if (!source || (current !== source && current !== knownTranslation)) {
            saved[attribute] = current;
          }
          const original = saved[attribute] || current;
          element.setAttribute(attribute, translateValue(original, language));
        });
        originalAttributes.current.set(element, saved);
      });
    };

    let frame = requestAnimationFrame(translate);
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(translate);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside LanguageProvider.");
  }
  return context;
}